import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from './base/service.base';
import { SimpleTopic, Topic } from '../utils/topic-utils';
import { Observable, tap } from 'rxjs';
import { ErrorCode } from '../constants';
import { ErrorRecord } from '@app/origami-api/utils/error-utils';
import { Converter } from '@app/shared/utils/converter';
import { DateFormatStore } from '@app/session/date-format-store';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from './base/request-proxy/cas-auth.request-proxy.class';
import { TimeZoneService } from '@app/services/time-zone.service';
import { FieldsDependency } from '@app/shared/forms/dynamic-form/fields.service';
import { TopicServiceModel, TopicServiceOptions } from '@app/origami-api/models/topic-service.model';
import { RiskScoreInfo } from './risk.service';

@Injectable()
export class TopicService extends ServiceBase {
  serviceUrl = 'TopicService.svc';

  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
  ) {
    super(backend, requestProxy);
  }

  private readonly lockTopicSilentErrors: number[] = [
    ErrorCode.TOPIC_LOCKED_BY_CURRENT_USER,
    ErrorCode.TOPIC_LOCKED_BY_ANOTHER_USER,
    ErrorCode.TOPIC_IS_NOT_LOCKED,
  ];

  getTopicTypes(
    body: TopicServiceOptions.GetTopicTypes = {},
    ...params
  ): Observable<OrigamiResponse<TopicServiceModel.TopicType[]>> {
    return this.origamiPost('/GetTopicTypes', body, ...params);
  }

  createTopic(
    data: TopicServiceOptions.CreateTopic,
    silentErrors?: ErrorCode[],
  ): Observable<OrigamiResponse<{ ErrorInfo: any; Id: number }>> {
    return this.origamiPost('/CreateTopic', data, null, silentErrors, true);
  }

  unlinkTopics(
    options: TopicServiceOptions.LinkTopics,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<{ ErrorRecords: any[] }>> {
    return this.origamiPost('/UnlinkTopics', options, null, silentErrorCodes, true);
  }

  linkTopics(
    options: TopicServiceOptions.LinkTopics,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<{ ErrorRecords: any[] }>> {
    return this.origamiPost('/LinkTopics', options, null, silentErrorCodes, true);
  }

  deleteTopic(siteId: number, topicId: number, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/DeleteTopic', { siteId, topicId }, null, silentErrorCodes, true);
  }

  deleteTopicTree(siteId: number, topicId: number): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/DeleteTopic', { siteId, topicId }, null, null, true);
  }

  getTopic(
    siteId: number,
    topicId: number,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
    includeQueryTickets?: boolean,
    includeWorkflow?: boolean,
  ): Observable<OrigamiResponse<Topic>> {
    return this.origamiPost(
      '/GetTopic',
      { siteId, topicId, includeQueryTickets, includeWorkflow },
      null,
      silentErrorCodes,
      forceRequest,
    ).pipe(
      tap((res) => {
        if (res.Success) {
          const result = res.Result;
          result.QueryTickets?.forEach((queryTicket) => {
            queryTicket.CreatedDateString = this.formatDate(queryTicket.CreatedDate);
            queryTicket.LastUpdatedDateString = this.formatDate(queryTicket.LastUpdatedDate);
            queryTicket.QueryTicketActivities.forEach((activity) => {
              activity.CreatedDateString = this.formatDate(activity.CreatedDate);
            });
          });
        }
      }),
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.fromCSharpString(dateString);
    return this.timeZoneService.formatDate(date, DateFormatStore.luxonDateTimeFormatWithTime);
  }

  updateTopic(
    options: TopicServiceOptions.UpdateTopic,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TopicServiceModel.TopicError[]>> {
    return this.origamiPost('/UpdateTopic', options, null, silentErrorCodes, true);
  }

  updateTopicFields(
    options: TopicServiceOptions.UpdateTopicFields,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TopicServiceModel.TopicError[]>> {
    return this.origamiPost('/UpdateTopicFields', options, null, silentErrorCodes, true);
  }

  saveTopicFields(data: TopicServiceOptions.SaveTopicFields, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/SaveTopicFields', data, null, silentErrorCodes, true);
  }

  saveTopicAttachments(data: TopicServiceOptions.SaveTopicAttachments) {
    return this.origamiPost('/SaveTopicAttachments', data, null, null, true);
  }

  /**
   * Accepts TopicId or SiteId doesn't matter what will be used.
   */
  setFavoriteTopics(data: TopicServiceOptions.SetFavoriteTopics): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/SetFavoriteTopics', data, null, null, true);
  }

  /**
   * Accepts TopicId or SiteId doesn't matter what will be used.
   */
  deleteFavoriteTopics(body: TopicServiceOptions.DeleteFavoriteTopics): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/DeleteFavoriteTopics', body, null, null, true);
  }

  shareFilter(siteId: number, topicId: number, shareType: number): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/ShareFilter', { siteId, topicId, shareType }, null, null, true);
  }

  deleteFilter(options: { siteId: number; topicId: number }) {
    return this.origamiPost('/DeleteFilter', options, null, null, true);
  }

  updateFilter(options: { siteId: number; topic: SimpleTopic }): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/UpdateFilter', options, null, null, true);
  }

  createFilter(options: {
    siteId: number;
    topic: SimpleTopic;
  }): Observable<OrigamiResponse<{ Id: number; ErrorInfo: any }>> {
    return this.origamiPost('/CreateFilter', options, null, null, true);
  }

  getTopicsByTypeName(
    body: TopicServiceOptions.GetTopicsByTypeName,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<Topic[]>> {
    return this.origamiPost('/GetTopicsByTypeName', body, options, silentErrorCodes, forceRequest);
  }

  // A non-empty result blocks the deletion: each entry names the topic and what still points at it
  checkEntitiesForReferences(
    body: TopicServiceOptions.CheckEntitiesForReferences,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/CheckEntitiesForReferences', body, null, null, true);
  }

  setTopicAccess(body: TopicServiceOptions.SetAccessRightInfo): Observable<OrigamiResponse<null>> {
    return this.origamiPost('/SetTopicAccess', body, null, null, true);
  }

  getTopicAccess(body: TopicServiceOptions.GetTopicAccess): Observable<OrigamiResponse<null>> {
    return this.origamiPost('/GetTopicAccess', body, null, null, true);
  }

  revokeTopicAccess(body: TopicServiceOptions.RevokeAccessRightInfo) {
    return this.origamiPost('/RevokeTopicAccess', body, null, null, true);
  }

  lockTopic(data: TopicServiceOptions.LockTopic) {
    return this.origamiPost('/LockTopic', data, null, this.lockTopicSilentErrors, true);
  }

  unlockTopic(data: TopicServiceOptions.UnlockTopic) {
    return this.origamiPost('/UnlockTopic ', data, null, this.lockTopicSilentErrors, true);
  }

  checkoutTopicForEditOnline(body: TopicServiceOptions.CheckoutTopicForEditOnline) {
    return this.origamiPost('/CheckoutTopicForEditOnline', body, null, null, true);
  }

  convertTopicReview(body: TopicServiceOptions.CheckoutTopicForEditOnline) {
    return this.origamiPost('/ConvertTopicReview', body, null, null, true);
  }

  checkinTopicForEditOnline(body: TopicServiceOptions.CheckinTopicForEditOnline, silentErrorCodes?: ErrorCode[]) {
    return this.origamiPost('/CheckinTopicForEditOnline', body, null, silentErrorCodes, true);
  }

  getTopicAcrossDomainSecurity(
    body: TopicServiceOptions.GetTopicAcrossDomainSecurity,
    ...params
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetTopicAcrossDomainSecurity', body, ...params);
  }

  getEffectiveTopicAccess(
    body: TopicServiceOptions.GetEffectiveTopicAccess,
    ...params
  ): Observable<OrigamiResponse<TopicServiceModel.EffectiveTopicAccessModel[]>> {
    return this.origamiPost('/GetEffectiveTopicAccess', body, ...params);
  }

  changeCheckoutType(body: TopicServiceOptions.ChangeCheckoutType): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/ChangeCheckoutType', body, null, null, true);
  }

  getTopicFileList(
    body: TopicServiceOptions.GetTopicFileList,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TopicServiceModel.TopicFile[]>> {
    return this.origamiPost('/GetTopicFileList', body, null, silentErrorCodes, true);
  }

  createProjectUrl(
    data: TopicServiceOptions.CreateTopic,
    silentErrors?: ErrorCode[],
  ): Observable<OrigamiResponse<{ ErrorInfo: any; Id: number }>> {
    return this.origamiPost('/CreateProjectUrl', data, null, silentErrors, true);
  }

  updateProjectUrl(options: TopicServiceOptions.UpdateTopicFields): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/UpdateProjectUrl', options, null, null, true);
  }

  deleteProjectUrl(
    siteId: number,
    topicId: number,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/DeleteProjectUrl', { siteId, topicId }, null, silentErrorCodes, true);
  }

  createBatch(options: TopicServiceOptions.CreateBatch): Observable<OrigamiResponse<number>> {
    return this.origamiPost('/CreateBatch', options, null, null, true);
  }

  getFieldSuggestions(options: TopicServiceOptions.GetFieldSuggestions): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetTopicFieldSuggestions', options, null, null, true);
  }

  getManualRelationTypes(options: TopicServiceOptions.GetManualRelationTypes): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetTopicRelationshipTypes', options, null, null, true);
  }

  getCustomFieldsDependencies(
    options: TopicServiceOptions.GetCustomFieldsDependenciesOptions,
  ): Observable<OrigamiResponse<FieldsDependency[]>> {
    return this.origamiPost('/GetCustomFieldsDependencies', options, null, null, true);
  }
  setCustomFieldsDependencies(
    options: TopicServiceOptions.SetCustomFieldsDependenciesOptions,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/SetCustomFieldsDependencies', options, null, null, true);
  }

  relateDocuments(options: TopicServiceOptions.RelateTopics): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/AddTopicsToRelationshipTypes', options, null, null, true);
  }

  removeDocumentsFromRelationType(options: TopicServiceOptions.RelateTopics): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/RemoveTopicsFromRelationshipTypes', options, null, null, true);
  }

  getRiskScoreInfo(siteId: number, topicId: number): Observable<OrigamiResponse<RiskScoreInfo>> {
    return this.origamiPost('/GetRiskScoreInfo', { siteId, topicId }, null, undefined, true);
  }
}
